import { BlockStack, Button, InlineError, LegacyStack, OptionList, Popover } from '@shopify/polaris'
import { useCallback, useEffect, useState } from 'react'

import { AdaptiveModal } from '../common/AdaptiveModal'
import { backendFetchResult, Result } from '../common/api'
import { resultErrorField, useToggle } from '../common/helpers'
import {
  Workflow,
  WorkflowAction,
  WorkflowActionType,
  WorkflowInternalActionType,
  WorkflowTriggerType,
} from '../common/workflow-models'
import { actionOptions, workflowActionDefaults } from '../workflow-page/ActionsCard'
import { ActionsForm } from '../workflow-page/ActionsForm'
import {
  convertWorkflowToFormData,
  createOrUpdateWorkflow,
  initialWorkflowFormData,
  WorkflowFormData,
} from '../workflow-page/api'

interface WorkflowActionsModalProps {
  open: boolean
  onClose: () => void
  workflow: Workflow | null
  sellingPlanId: string
  onSaved: () => void
}

export const WorkflowActionsModal = ({
  open,
  onClose,
  workflow,
  sellingPlanId,
  onSaved,
}: WorkflowActionsModalProps) => {
  const [actions, setActions] = useState<WorkflowAction[]>([])
  const [saveResult, setSaveResult] = useState<Result<any> | null>(null)
  const [saving, setSaving] = useState(false)
  const [actionPopoverActive, toggleActionPopover] = useToggle(false)

  useEffect(() => {
    if (open) {
      setActions(workflow?.actions ?? [])
      setSaveResult(null)
    }
  }, [open, workflow])

  const triggers: WorkflowTriggerType[] = workflow
    ? workflow.triggers
    : ['subscription-secondary-price-adjustment-applied']

  const buildWorkflowFormData = useCallback((): WorkflowFormData => {
    if (workflow) {
      const formData = convertWorkflowToFormData(workflow)
      formData.actions = actions
      return formData
    }

    const formData = initialWorkflowFormData({
      secondaryPriceAdjustmentSellingPlanId: sellingPlanId,
    })
    formData.actions = actions
    return formData
  }, [workflow, actions, sellingPlanId])

  const handleDone = useCallback(async () => {
    // No actions and no existing workflow — nothing to do
    if (actions.length === 0 && !workflow) {
      onClose()
      return
    }

    setSaving(true)

    // Existing workflow with all actions removed — delete it
    if (actions.length === 0 && workflow) {
      const result = await backendFetchResult('DELETE', `/workflows/${workflow.id}`)
      setSaving(false)
      setSaveResult(result)

      if (result.status === 'success') {
        onSaved()
        onClose()
      }
      return
    }

    const formData = buildWorkflowFormData()

    const result = await createOrUpdateWorkflow(
      formData,
      workflow ? String(workflow.id) : undefined
    )
    setSaving(false)
    setSaveResult(result)

    if (result.status === 'success') {
      onSaved()
      onClose()
    }
  }, [workflow, actions, buildWorkflowFormData, onSaved, onClose])

  const error = resultErrorField(saveResult, 'actions')

  return (
    <AdaptiveModal
      title={workflow ? 'Edit additional actions' : 'Add additional actions'}
      open={open}
      onClose={onClose}
      primaryAction={{ content: 'Done', onAction: handleDone, loading: saving }}
      sectioned
    >
      <BlockStack gap="300">
        <ActionsForm
          actions={actions}
          triggers={triggers}
          onChange={setActions}
          saveResult={saveResult}
          errorPrefix=""
        />
        <LegacyStack vertical>
          <Popover
            active={actionPopoverActive}
            activator={
              <Button onClick={toggleActionPopover} disclosure>
                Add action
              </Button>
            }
            onClose={toggleActionPopover}
            fluidContent
          >
            <OptionList
              onChange={(selected: Exclude<WorkflowActionType, WorkflowInternalActionType>[]) => {
                setActions([...actions, [selected[0], workflowActionDefaults[selected[0]]]])
                toggleActionPopover()
              }}
              options={actionOptions}
              selected={[]}
            />
          </Popover>
          {error && <InlineError message={error} fieldID="actions" />}
        </LegacyStack>
      </BlockStack>
    </AdaptiveModal>
  )
}
